/*
 * The article editor.
 *
 * Four panels, and the split between them follows the columns they write rather
 * than any visual preference:
 *
 *   Properties     kb.Article - slug, product, category tags. Guarded by the
 *                  article's rowversion, so it is one optimistic-concurrency
 *                  unit. A change to either half of the ADDRESS - the slug or
 *                  the product - also rewrites the other articles' links to this
 *                  one; ArticleProperties says how, and why this article's own
 *                  draft is repaired here rather than on the server.
 *   Publishing     kb.Article - IsVisible and PublishAtUtc. Same row, but a
 *                  separate panel because a publish is an act, not a form edit.
 *   Article        kb.ArticleRevision - the change note and the two saves.
 *   Text           kb.ArticleRevision - title and body, and the images the body
 *                  refers to. Append-only, so it is guarded by the base revision
 *                  number instead.
 *
 * Article and Text write the SAME row and are one save: the note, the title and
 * the body all go into one new revision. They are two panels because of where
 * they sit - Article stacks under Publishing in the narrow column, Text runs
 * full width below - so neither of them owns the draft. ArticleBody does, and
 * renders both.
 *
 * Two things are worth stating because a well-meaning simplification would break
 * them:
 *
 *   - The slug sits with the properties, not with the text, even though an author
 *     thinks of the two together. It is a column on kb.Article carrying that row's
 *     rowversion; the title is a column on the revision. One save cannot cover both
 *     without inventing a conflict story for two different concurrency tokens. The
 *     product now sits there for the same reason and one stronger: it is the other
 *     half of the address, so the two fields are one decision.
 *   - There are two save buttons. "Save as draft" appends a revision without
 *     promoting it, which UX_ArticleRevision_Current (unique on ArticleId WHERE
 *     IsCurrent = 1) permits: exactly one revision is live, and any number may sit
 *     ahead of it. That is how a live article is edited over several days without
 *     readers seeing the work in progress. Collapsing the two buttons into one
 *     would remove the only thing the revision table exists to express.
 *
 * There is no "withdraw the published text" action. With one visibility flag, "Hide
 * from readers" is the complete and reversible way to take an article down, and it
 * keeps the text; leaving an article with no current revision would make it
 * indistinguishable from one nobody has written yet.
 *
 * Images sit with the text, in the Text panel, because that is what they belong to -
 * but they are attached to the ARTICLE, not to a revision. kb.ArticleImage is keyed
 * (ArticleId, FileName), which is deliberate: revision-scoped images would copy
 * every binary payload on every save. The consequence is worth stating rather than
 * discovering, so the delete dialog states it: an old revision renders today's
 * image, and deleting an image removes it from every revision that showed it. There
 * is direct precedent - a revision does not capture what the slug was at the time
 * either.
 *
 * Attachments (S47) sit under the images in the same card, on the same terms and
 * one more: the list is the ARTICLE's (kb.ArticleAttachment), but the files are
 * a library other articles share, so this page lists and unlists them and leaves
 * replacing and deleting a file to the library page.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  AdminArticleDetail,
  AdminAttachment,
  ArticleImage,
  ArticleLinkSlugTarget,
  ArticleReference,
  DeleteResult,
} from '@accotool-kb/shared';
import { articleHref, renameArticleLinks } from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { useAction, useAsync } from '../hooks/useAsync.js';
import { DateTime, ErrorBanner, Field, Loading, PublishingStatusBadge } from '../components/ui.js';
import type { MetadataBundle } from '../api/adminApi.js';
import { ApiError } from '../api/client.js';
import { useMessages } from '../i18n/language.js';
import { richText } from '../i18n/richText.js';
import { ArticleProperties } from './components/ArticleProperties.js';
import { PublishControls } from './components/PublishControls.js';
import { ArticleSaveBox } from './components/ArticleSaveBox.js';
import { MarkdownEditor, type MarkdownEditorHandle } from './components/MarkdownEditor.js';
import { ArticleImagesPanel } from './components/ArticleImagesPanel.js';
import { ArticleImagePreviewsProvider } from './components/ArticleImagePreviewsProvider.js';
import { ArticleAttachmentsPanel } from './components/ArticleAttachmentsPanel.js';
import { RevisionHistoryPanel } from './components/RevisionHistoryPanel.js';
import { LinkedFromPanel } from './components/LinkedFromPanel.js';
import { DeleteImpactDialog, type DeleteTarget } from './components/DeleteImpactDialog.js';

export function ArticleEditorPage(): JSX.Element {
  const t = useMessages();
  const words = t.admin.articles.editor;
  const api = useAdminApi();
  const params = useParams();
  const articleId = params.articleId ?? '';

  const state = useAsync(() => api.getArticle(articleId), [articleId]);
  const metadata = useAsync(() => api.metadata(), []);

  /*
   * What links here, requested BESIDE the article rather than inside it.
   *
   * Finding these means the server scanning the current and latest revision of
   * every other article, which grows with the knowledge base and not with this
   * article - it used to ride on the detail response, and the editor waited
   * for it on every open and after every save. Now the two requests go out
   * together, the text paints when the article arrives, and the two cards that
   * read this list (Linked from, and the Properties rename warning) fill in
   * when it lands. A null here means "not known yet", and both of them say so
   * rather than reading it as "nothing links here".
   *
   * Re-fetched when a save moves the ADDRESS: the server rewrites the other
   * articles' [[kb:old-product/old-slug]] tokens in the same transaction, so a
   * list read before the rename shows tokens that no longer exist and would
   * count them against an address that is no longer this article's.
   *
   * Two lists since S42 (2026-10-12): `items`, the other articles, and
   * `releaseNotes`, the release notes whose text links here - the server
   * rewrites both on a rename and clears both on a delete. The rename warning
   * reads the articles; the Linked-from card shows both.
   */
  const [referencesKey, setReferencesKey] = useState(0);
  const references = useAsync(
    () => api.listArticleReferences(articleId),
    [articleId, referencesKey],
  );

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deletedNotice, setDeletedNotice] = useState<DeleteResult | null>(null);
  const [historyKey, setHistoryKey] = useState(0);

  const article = state.data;

  const handleUpdated = (next: AdminArticleDetail): void => {
    if (article && (next.slug !== article.slug || next.product.slug !== article.product.slug)) {
      setReferencesKey((key) => key + 1);
    }
    state.setData(next);
  };

  if (state.loading && !article) return <Loading label={t.article.loading} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!article) return <Loading />;

  // The article was deleted from this page: there is nothing left to edit.
  if (deletedNotice) {
    return (
      <div className="card">
        <h1>{words.deleted}</h1>
        <p>{words.deletedBody(deletedNotice.targetLabel)}</p>
        <ul>
          {deletedNotice.performed.map((step) => (
            <li key={step.code}>{step.description}</li>
          ))}
        </ul>
        <Link className="button button-primary" to="/admin/articles">
          {words.backToArticles}
        </Link>
      </div>
    );
  }

  const heading = article.title ?? t.admin.articles.shared.untitled;

  return (
    <>
      <div className="page-header">
        <div>
          <nav className="breadcrumb">
            <Link to="/admin/articles">{t.admin.articles.shared.articles}</Link>
          </nav>
          <h1>{heading}</h1>
          <p className="faint">
            {richText(words.createdLine, {
              created: <DateTime value={article.createdAtUtc} />,
              modified: <DateTime value={article.modifiedAtUtc} />,
            })}
          </p>
        </div>

        <div className="button-row">
          <PublishingStatusBadge status={article.status} />
          {article.status === 'live' && (
            <a
              className="button button-small"
              href={articleHref(article.product.slug, article.slug)}
              target="_blank"
              rel="noreferrer"
            >
              {words.viewOnSite}
            </a>
          )}
          <button
            type="button"
            className="button button-small button-quiet"
            onClick={() =>
              setDeleteTarget({
                kind: 'article',
                targetId: article.articleId,
                label: heading,
              })
            }
          >
            {words.deleteArticle}
          </button>
        </div>
      </div>

      {/*
        Keyed on the article id, which keeps the lifetime of the draft state local
        to the component that owns it.
        Belt-and-braces today: AdminLayout renders the outlet inside
        <section key={location.pathname}>, so moving between two articles already
        unmounts this whole page and nothing survives. But that is a detail of the
        shell two components away, and what turns on it here is not obvious - if a
        draft ever did survive a navigation, opening a second article whose latest
        revision happened to carry the SAME number would leave the first
        article's text in the boxes, because the re-seed effect in ArticleBody is
        keyed on that number and would not fire. The key states that requirement
        where the state is, instead of leaving it resting on the shell.
      */}
      <ArticleBody
        key={article.articleId}
        article={article}
        metadata={metadata.data}
        incomingReferences={references.data?.items ?? null}
        onUpdated={handleUpdated}
        onRevisionsChanged={() => setHistoryKey((key) => key + 1)}
      />

      <LinkedFromPanel
        references={references.data?.items ?? null}
        releaseNotes={references.data?.releaseNotes ?? null}
        error={references.error}
        onRetry={references.reload}
      />

      <div className="card">
        <RevisionHistoryPanel
          articleId={article.articleId}
          currentRevisionNumber={article.currentRevisionNumber}
          onChanged={() => void api.getArticle(article.articleId).then(state.setData)}
          reloadKey={historyKey}
        />
      </div>

      <DeleteImpactDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={(result) => {
          setDeletedNotice(result);
          setDeleteTarget(null);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// One new revision: the title and body being typed, the note that describes the
// change, and the two saves. Split across two cards, in two columns, but a
// single unit of work - which is why the state lives here and not in either card
// ---------------------------------------------------------------------------

function ArticleBody({
  article,
  metadata,
  incomingReferences,
  onUpdated,
  onRevisionsChanged,
}: {
  article: AdminArticleDetail;
  metadata: MetadataBundle | null;
  /** Null until the page's separate request for them has answered. */
  incomingReferences: ArticleReference[] | null;
  onUpdated: (next: AdminArticleDetail) => void;
  onRevisionsChanged: () => void;
}): JSX.Element {
  const t = useMessages();
  const words = t.admin.articles.editor;
  const api = useAdminApi();
  const save = useAction();

  const latest = article.latestRevision;

  const [title, setTitle] = useState(latest?.title ?? '');
  const [content, setContent] = useState(latest?.content ?? '');
  const [changeNote, setChangeNote] = useState('');
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  // Re-seed when the server hands back a newer revision - after a save of our own,
  // or after someone else's change was picked up by a reload. Keyed on the revision
  // number rather than the text, so a re-render never discards what is being typed.
  // Switching to a different ARTICLE is handled by the key on this component, not
  // here; see the comment at the call site.
  useEffect(() => {
    setTitle(latest?.title ?? '');
    setContent(latest?.content ?? '');
    setChangeNote('');
  }, [latest?.revisionNumber]);

  const dirty = useMemo(
    () => title !== (latest?.title ?? '') || content !== (latest?.content ?? ''),
    [title, content, latest],
  );

  const titleError = title.trim().length === 0 ? t.admin.articles.shared.titleRequired : undefined;

  // Warn before losing an unsaved edit - the one place in this UI where a stray
  // navigation destroys work that only exists in the browser.
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const saveRevision = async (publish: boolean) => {
    if (titleError) return;

    // Captured before the save, so the notice can say which revision readers were
    // on rather than the one they have just been moved to.
    const previousCurrent = article.currentRevisionNumber;

    const result = await save.run(() =>
      api.saveRevision(article.articleId, {
        title: title.trim(),
        content,
        changeNote: changeNote.trim() || null,
        publish,
        // The revision this editor opened. If somebody else has saved since, the
        // server refuses rather than stacking a revision on work nobody has seen.
        baseRevisionNumber: article.latestRevisionNumber ?? 0,
      }),
    );

    if (result) {
      onUpdated(result.article);
      onRevisionsChanged();
      setSavedNotice(
        publish
          ? words.savedAndPublished(result.revision.revisionNumber)
          : words.savedAsDraft(result.revision.revisionNumber, previousCurrent),
      );
    }
  };

  /*
   * The latest text as typed, for a handler that runs after an await and whose
   * closure may predate the last keystroke. Assigned every render, so it is as
   * current as the last commit.
   */
  const contentRef = useRef(content);
  contentRef.current = content;

  /*
   * The Properties card changed the ADDRESS - the slug, the product, or both. Any
   * [[kb:old-product/old-slug]] in THIS draft would now point at nothing. The
   * server leaves this article's revisions alone precisely so nothing is appended
   * under an editor that is open - a new revision number would re-seed the draft
   * and discard what is being typed - so the draft is repaired here instead, and
   * becomes an unsaved change the author saves as their own revision.
   *
   * An article CAN link to itself; that is why this exists at all. The pair goes
   * through renameArticleLinks unchanged from the Properties card, which is the
   * same call the server makes against every other article.
   */
  const renameSelfLinks = (
    previous: ArticleLinkSlugTarget,
    next: ArticleLinkSlugTarget,
  ): number => {
    const result = renameArticleLinks(contentRef.current, previous, next);
    if (result.renamed > 0) setContent(result.content);
    return result.renamed;
  };

  return (
    <>
      <div className="split-two" style={{ marginBottom: '1.5rem' }}>
        <ArticleProperties
          article={article}
          metadata={metadata}
          incomingReferences={incomingReferences}
          onUpdated={onUpdated}
          onAddressRenamed={renameSelfLinks}
        />

        {/*
          The right column stacks two cards. The wrapper is what makes that a
          column at all - .split-two's children ARE its grid cells - and it is
          also what restores the margin between them: the
          `:where(.split-two) > .card + .card` reset in the stylesheet removes
          the stacking margin from cards laid out side by side, and these two are
          not side by side.
        */}
        <div>
          <PublishControls article={article} onUpdated={onUpdated} />
          <ArticleSaveBox
            changeNote={changeNote}
            onChangeNote={setChangeNote}
            onSave={saveRevision}
            saving={save.running}
            blocked={Boolean(titleError)}
            dirty={dirty}
            latest={latest}
          />
        </div>
      </div>

      {/*
        The three save outcomes stay full width, below the columns, even though
        the buttons that produce them now sit in the narrow one. Each is a block
        of prose - the conflict notice is two sentences and an instruction - and
        squeezed into a 1fr column they would be a tall thin wall of text next to
        the control they describe.
      */}
      {savedNotice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">{savedNotice}</div>
          <div className="notice-actions">
            <button
              type="button"
              className="button button-quiet"
              onClick={() => setSavedNotice(null)}
            >
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      {save.error && <ErrorBanner error={save.error} onDismiss={save.clearError} />}

      {save.error instanceof ApiError && save.error.isConcurrencyConflict && (
        <div className="notice notice-warning">
          <div className="notice-body">
            <strong>{words.outOfDate}</strong>
            <p style={{ marginBottom: 0 }}>{words.outOfDateHelp}</p>
          </div>
        </div>
      )}

      <ArticleText
        article={article}
        title={title}
        onTitle={setTitle}
        content={content}
        onContent={setContent}
        titleError={titleError}
        saving={save.running}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// The Text box: the title and body of the revision, and the images the body
// refers to
// ---------------------------------------------------------------------------

function ArticleText({
  article,
  title,
  onTitle,
  content,
  onContent,
  titleError,
  saving,
}: {
  article: AdminArticleDetail;
  title: string;
  onTitle: (next: string) => void;
  content: string;
  onContent: (next: string) => void;
  titleError: string | undefined;
  saving: boolean;
}): JSX.Element {
  const t = useMessages();
  const words = t.admin.articles.editor;

  /*
   * The attachments, held here rather than read straight from the prop.
   *
   * An upload or a delete changes them without touching the revision, so the list
   * has to be able to move on its own - a save is not involved and there is nothing
   * to reload. Re-seeded whenever the server hands back a fresh article, which is
   * how somebody else's upload arrives.
   */
  const [images, setImages] = useState<ArticleImage[]>(article.images ?? []);
  useEffect(() => setImages(article.images ?? []), [article.images]);

  // The attachment list (S47), held the same way and for the same reason: adding
  // and removing are saved as they happen, outside the revision.
  const [attachments, setAttachments] = useState<AdminAttachment[]>(article.attachments ?? []);
  useEffect(() => setAttachments(article.attachments ?? []), [article.attachments]);

  // The editor owns the caret, so inserting a reference has to go through it.
  const editor = useRef<MarkdownEditorHandle>(null);

  return (
    <div className="card">
      <div className="card-header">
        <h3>{words.text}</h3>
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {article.hasUnpublishedDraft && (
            <span className="badge badge-draft">{words.draftBadge(article.latestRevisionNumber)}</span>
          )}
          {article.latestRevisionNumber === null && (
            <span className="badge badge-unwritten">{words.nothingWritten}</span>
          )}
        </span>
      </div>

      {article.hasUnpublishedDraft && (
        <div className="notice notice-info">
          <div className="notice-body">
            <strong>
              {words.editingDraft(article.latestRevisionNumber, article.currentRevisionNumber)}
            </strong>
            <p style={{ marginBottom: 0 }}>{words.editingDraftHelp}</p>
          </div>
        </div>
      )}

      <Field label={t.admin.articles.shared.title} htmlFor="title" required error={titleError}>
        <input
          id="title"
          type="text"
          value={title}
          maxLength={300}
          onChange={(event) => onTitle(event.target.value)}
        />
      </Field>

      {/*
        One cache of image bytes for both: the preview and the thumbnails used to
        download the same picture each, concurrently, whenever it was both in the
        text and scrolled into view. Under this provider an image is fetched once
        for the page and re-used wherever it shows.
      */}
      <ArticleImagePreviewsProvider articleId={article.articleId}>
        <MarkdownEditor
          ref={editor}
          value={content}
          onChange={onContent}
          articleId={article.articleId}
          disabled={saving}
          images={images}
          onImagesChanged={setImages}
        />

        <ArticleImagesPanel
          articleId={article.articleId}
          content={content}
          images={images}
          onImagesChanged={setImages}
          onInsert={(fileName) => editor.current?.insertImageReference(fileName)}
        />
      </ArticleImagePreviewsProvider>

      {/*
        Under the images, in the same card: both are what the text can point at,
        and "Insert link" needs the editor's caret, which this card owns.
      */}
      <ArticleAttachmentsPanel
        articleId={article.articleId}
        content={content}
        attachments={attachments}
        onAttachmentsChanged={setAttachments}
        onInsertLink={(token) => editor.current?.insertAtCaret(token)}
      />
    </div>
  );
}
